import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiPause, FiPlay } = FiIcons;

const MobileTimer = ({ currentPlayer, gameStatus, onTimeUp }) => {
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes in seconds
  const [isActive, setIsActive] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    setTimeLeft(120);
    setIsActive(gameStatus === 'playing' || gameStatus === 'check');
    setIsPaused(false);
  }, [currentPlayer, gameStatus]);

  useEffect(() => {
    let interval = null;
    
    if (isActive && !isPaused && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(time => {
          if (time <= 1) {
            onTimeUp();
            return 0;
          }
          return time - 1;
        });
      }, 1000);
    } else if (timeLeft === 0) {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [isActive, isPaused, timeLeft, onTimeUp]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    if (timeLeft <= 30) return 'text-red-400 border-red-400 bg-red-500/10';
    if (timeLeft <= 60) return 'text-yellow-400 border-yellow-400 bg-yellow-500/10';
    return 'text-green-400 border-green-400 bg-green-500/10';
  };

  const getProgressPercentage = () => {
    return (timeLeft / 120) * 100;
  };

  const togglePause = () => {
    if (gameStatus === 'playing' || gameStatus === 'check') {
      setIsPaused(!isPaused);
    }
  };

  return (
    <div className="w-full">
      {/* Timer Display */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-black/20 backdrop-blur-sm rounded-xl p-4 border border-white/20"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiClock} className="text-white text-xl" />
            <span className="text-white font-semibold">
              {currentPlayer === 'white' ? 'White' : 'Black'}
            </span>
          </div>
          
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={togglePause}
            className="p-2 bg-white/10 rounded-lg text-white"
            disabled={!(gameStatus === 'playing' || gameStatus === 'check')}
          >
            <SafeIcon icon={isPaused ? FiPlay : FiPause} />
          </motion.button>
        </div>

        {/* Timer */}
        <div className="text-center">
          <motion.div
            className={`text-4xl font-mono font-bold border-2 rounded-lg p-4 mb-3 ${getTimerColor()}`}
            animate={{
              scale: timeLeft <= 10 && !isPaused ? [1, 1.05, 1] : 1,
            }}
            transition={{
              duration: 1,
              repeat: timeLeft <= 10 && !isPaused ? Infinity : 0
            }}
          >
            {formatTime(timeLeft)}
          </motion.div>

          {/* Progress Bar */}
          <div className="bg-gray-700 rounded-full h-3 overflow-hidden mb-3">
            <motion.div
              className={`h-full transition-colors duration-300 ${
                timeLeft <= 30 ? 'bg-red-400' : 
                timeLeft <= 60 ? 'bg-yellow-400' : 
                'bg-green-400'
              }`}
              initial={{ width: '100%' }}
              animate={{ width: `${getProgressPercentage()}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>

          {/* Status Messages */}
          {isPaused && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-blue-300 text-sm font-semibold"
            >
              Game Paused
            </motion.p>
          )}
          
          {timeLeft <= 30 && !isPaused && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-red-300 text-sm font-semibold"
            >
              Time running out!
            </motion.p>
          )}
        </div>
      </motion.div>

      {/* Mobile-specific timer info */}
      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div className="bg-white/5 rounded-lg p-2">
          <div className="text-lg font-bold text-white">2:00</div>
          <div className="text-xs text-gray-400">Per Move</div>
        </div>
        <div className="bg-white/5 rounded-lg p-2">
          <div className="text-lg font-bold text-blue-400">{Math.round(getProgressPercentage())}%</div>
          <div className="text-xs text-gray-400">Time Left</div>
        </div>
        <div className="bg-white/5 rounded-lg p-2">
          <div className={`text-lg font-bold ${isPaused ? 'text-blue-400' : 'text-green-400'}`}>
            {isPaused ? 'Paused' : 'Active'}
          </div>
          <div className="text-xs text-gray-400">Status</div>
        </div>
      </div>
    </div>
  );
};

export default MobileTimer;